import { LayoutGrid, List, Loader2, Search, X } from 'lucide-react';

import type { AdminStoreListParams } from '@/client/types/stores';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const selectClass =
  'rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export type StoreSort = `${NonNullable<AdminStoreListParams['sort']>}:${'asc' | 'desc'}`;
export type StoreView = 'cards' | 'list';
/** '' = any, '1' = on a plan, '0' = no plan. */
export type PlanFilter = '' | '0' | '1';

export const DEFAULT_SORT: StoreSort = 'date_added:desc';

const SORT_OPTIONS: { value: StoreSort; label: string }[] = [
  { value: 'date_added:desc', label: 'Newest' },
  { value: 'date_added:asc', label: 'Oldest' },
  { value: 'name:asc', label: 'Name A–Z' },
  { value: 'name:desc', label: 'Name Z–A' },
  { value: 'gmv:desc', label: 'Highest GMV' },
  { value: 'orders:desc', label: 'Most orders' },
  { value: 'date_plan_expires:asc', label: 'Plan expiring soonest' },
];

export const isStoreSort = (v: string | null): v is StoreSort => SORT_OPTIONS.some((o) => o.value === v);

const VIEWS: { value: StoreView; label: string; Icon: typeof List }[] = [
  { value: 'cards', label: 'Card view', Icon: LayoutGrid },
  { value: 'list', label: 'List view', Icon: List },
];

export const StoresFilters: React.FC<{
  search: string;
  onSearch: (v: string) => void;
  searchBusy: boolean;
  plan: PlanFilter;
  onPlan: (v: PlanFilter) => void;
  owner: string;
  onOwner: (v: string) => void;
  planTier: string;
  onPlanTier: (v: string) => void;
  /** Tier names in use; without them the tier filter is a free-text box. */
  planTiers?: string[];
  sort: StoreSort;
  onSort: (v: StoreSort) => void;
  view: StoreView;
  onView: (v: StoreView) => void;
  /** Shown only when something is filtered. */
  onReset?: () => void;
}> = ({ search, onSearch, searchBusy, plan, onPlan, owner, onOwner, planTier, onPlanTier, planTiers, sort, onSort, view, onView, onReset }) => (
  <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-3">
    <div className="min-w-[14rem] flex-1 space-y-1">
      <label htmlFor="store-search" className="block text-xs font-medium text-gray-500">Search</label>
      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <Input id="store-search" className="pl-9 pr-9" placeholder="Store name, handle or id…" value={search} onChange={(e) => onSearch(e.target.value)} />
        {searchBusy && <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-gray-400" aria-label="Loading" />}
      </div>
    </div>

    <div className="space-y-1">
      <label htmlFor="store-owner" className="block text-xs font-medium text-gray-500">Owner</label>
      <Input id="store-owner" className="w-52" placeholder="Name, email or id…" value={owner} onChange={(e) => onOwner(e.target.value)} />
    </div>

    <div className="space-y-1">
      <label htmlFor="store-plan" className="block text-xs font-medium text-gray-500">Plan</label>
      <select id="store-plan" className={selectClass} value={plan} onChange={(e) => onPlan(e.target.value as PlanFilter)}>
        <option value="">Any</option>
        <option value="1">On a plan</option>
        <option value="0">No plan</option>
      </select>
    </div>

    <div className="space-y-1">
      <label htmlFor="store-plan-tier" className="block text-xs font-medium text-gray-500">Plan tier</label>
      {planTiers?.length ? (
        <select id="store-plan-tier" className={selectClass} value={planTier} onChange={(e) => onPlanTier(e.target.value)}>
          <option value="">Any</option>
          {/* Keep a tier from a shared link selectable even if no store has it now. */}
          {[...new Set([...planTiers, ...(planTier ? [planTier] : [])])].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      ) : (
        <Input id="store-plan-tier" className="w-28" placeholder="any" value={planTier} onChange={(e) => onPlanTier(e.target.value)} />
      )}
    </div>

    <div className="space-y-1">
      <label htmlFor="store-sort" className="block text-xs font-medium text-gray-500">Sort</label>
      <select id="store-sort" className={selectClass} value={sort} onChange={(e) => onSort(e.target.value as StoreSort)}>
        {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>

    {onReset && (
      <button type="button" onClick={onReset} className="inline-flex items-center gap-1 rounded-md px-2 py-2 text-sm text-gray-600 hover:bg-gray-100">
        <X className="h-3.5 w-3.5" /> Reset
      </button>
    )}

    <div className="ml-auto inline-flex rounded-lg border border-gray-200 p-0.5" role="group" aria-label="View">
      {VIEWS.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          aria-label={label}
          aria-pressed={view === value}
          title={label}
          onClick={() => onView(value)}
          className={cn(
            'inline-flex h-8 w-9 items-center justify-center rounded-md transition-colors',
            view === value ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100',
          )}
        >
          <Icon className="h-4 w-4" />
        </button>
      ))}
    </div>
  </div>
);
